import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { KeyboardEvent, memo, ReactNode, RefObject } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { spacing, fontSize, borderRadius } from 'presentations/views/styles';
import {
  white,
  gray01,
  gray02,
  gray05,
  black,
  secondary,
  gray04,
  primary,
} from 'presentations/views/styles/color';
import FormLabel, {
  FormLabelHelp,
} from 'presentations/views/components/molecules/FormLabel';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import FormPaidLink from 'presentations/views/components/molecules/FormPaidLink';
import { PlanId } from 'domains/models/subscription/planId';

interface Props {
  type?: string;
  label?: string;
  description?: string | ReactNode;
  prefix?: ReactNode | string;
  suffix?: ReactNode | string;
  className?: string;
  placeholder?: string;
  value?: string | number | null;
  defaultValue?: string | null;
  isFullWidth?: boolean;
  isContainerFullWidth?: boolean;
  width?: number;
  size?: 'medium' | 'small';
  variant?: 'outlined' | 'standard' | 'filled';
  outlineColor?: string;
  isDisabled?: boolean;
  id?: string;
  name?: string;
  errors?: string[];
  isSelect?: boolean;
  isSelectNative?: boolean;
  helperText?: string;
  rows?: number;
  maxRows?: number;
  isMultiline?: boolean;
  children?: ReactNode;
  onChange?: any;
  onClick?: any;
  onBlur?: any;
  onKeyPress?: (e: KeyboardEvent) => void;
  onFocus?: any;
  isLoading?: boolean;
  isPaid?: boolean;
  isShowPaidLink?: boolean;
  plan?: PlanId;
  isRequired?: boolean;
  isNotRequired?: boolean;
  isAutoFocus?: boolean;
  inputRef?: RefObject<any> | null;
  autoComplete?: string;
  maxLength?: number;
  help?: FormLabelHelp;
}

const Component = memo((props: Props) => {
  const errors = props.errors ? props.errors : [];
  const isError = errors.length > 0;
  const selectProps: any = {};
  if (props.isSelect === true) {
    if (props.isSelectNative === undefined) {
      selectProps['native'] = true;
    } else {
      selectProps['native'] = props.isSelectNative;
    }
  }

  let description = null;
  if (props.isPaid && props.isShowPaidLink) {
    description = (
      <PaidLink>
        <FormPaidLink description={props.description} plan={props.plan} />
      </PaidLink>
    );
  } else {
    if (props.description) {
      description = <Description>{props.description}</Description>;
    }
  }

  return (
    <Container
      fullWidth={props.isContainerFullWidth === true}
      onClick={props.onClick}
    >
      {props.label && (
        <FormLabel
          isRequired={props.isRequired}
          isPaid={props.isPaid}
          isShowPaidLink={props.isShowPaidLink}
          plan={props.plan}
          isNotRequired={props.isNotRequired}
          help={props.help}
        >
          {props.label}
        </FormLabel>
      )}
      {description}
      <StyledTextField
        sx={{
          width: props.width,
        }}
        inputRef={props.inputRef}
        autoFocus={props.isAutoFocus}
        type={props.type || 'text'}
        placeholder={props.placeholder}
        size={props.size || 'medium'}
        disabled={props.isDisabled || props.isLoading}
        InputLabelProps={{
          shrink: false,
        }}
        className={props.className}
        variant={props.variant || 'outlined'}
        value={props.value === null ? undefined : props.value}
        defaultValue={props.defaultValue}
        fullWidth={props.isFullWidth}
        error={isError}
        id={props.id}
        name={props.name}
        rows={props.rows}
        maxRows={props.maxRows}
        multiline={props.isMultiline}
        select={props.isSelect}
        InputProps={{
          startAdornment: props.prefix && (
            <InputAdornment position="start">
              <Prefix>{props.prefix}</Prefix>
            </InputAdornment>
          ),
          endAdornment: props.suffix && (
            <InputAdornment position="end">
              <Suffix>{props.suffix}</Suffix>
            </InputAdornment>
          ),
        }}
        SelectProps={selectProps}
        onChange={props.onChange}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onKeyPress={props.onKeyPress}
        autoComplete={props.autoComplete}
        outlinecolor={props.outlineColor}
        inputProps={{
          maxLength: props.maxLength,
        }}
      >
        {props.children}
      </StyledTextField>
      {props.helperText && (
        <HelperText dangerouslySetInnerHTML={{ __html: props.helperText }} />
      )}
      {isError &&
        errors.map((errorText, i) => (
          <ErrorText key={i}>{errorText}</ErrorText>
        ))}
    </Container>
  );
});
export const Container = styled.span<{ fullWidth: boolean }>`
  .MuiOutlinedInput-root {
    height: 100% !important;
  }
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const StyledTextField = styled(TextField)<{
  outlinecolor?: string;
  disabled?: boolean;
  size?: string;
}>`
  .MuiInputBase-root {
    border-radius: ${borderRadius(6)};
    color: ${black};
    font-size: ${(props) => props.size === 'small' && fontSize(14)};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${gray02};
  }
  .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${secondary};
  }
  .MuiInput-underline.Mui-fucused {
    border-color: ${gray04};
    border: 1px solid ${primary};
  }
  .MuiInput-underline:before {
    border-color: ${gray04};
  }
  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom: 1px solid ${(props) => props.outlinecolor || primary};
  }
  .MuiInput-underline:after {
    border-color: ${(props) => props.outlinecolor || primary};
  }
  .MuiOutlinedInput-root.Mui-focused fieldset {
    border-color: ${(props) => props.outlinecolor || primary};
  }
  .MuiOutlinedInput-multiline.Mui-focused fieldset {
    border-color: ${(props) => props.outlinecolor || primary};
  }
  background-color: ${(props) => (props.disabled ? gray05 : white)};
  border-radius: ${borderRadius(6)};
`;

export const Description = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(16)};
`;
export const Prefix = styled.div`
  margin-right: ${spacing(8)};
  color: ${gray02};
  display: flex;
  align-items: center;
`;
export const Suffix = styled.span`
  margin-left: ${spacing(8)};
  color: ${gray02};
`;
const HelperText = styled.div`
  font-size: ${fontSize(12)};
  margin-top: ${spacing(8)};
  color: ${gray01};
`;
const PaidLink = styled.div`
  margin-bottom: ${spacing(16)};
`;

export default Component;
